import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, Plus, Upload, ImageIcon } from "lucide-react";
import { STUDIO, PHOTO_CATEGORIES } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "room9_admin_token";

export const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/check`, { headers: authHeaders() })
      .then(() => setChecking(false))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setChecking(false);
      });
  }, [token]);

  if (checking) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Loader2 className="animate-spin text-forest" size={28} />
      </div>
    );
  }

  return token ? <Dashboard onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} /> : <LoginForm onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
}

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      toast.success("Welcome back, Room 9 Admin");
      onLogin(res.data.token);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm bg-white border border-line-2 rounded-xl p-8 shadow-sm space-y-6"
      >
        <div className="text-center">
          <img src={STUDIO.logo} alt="Room 9" className="w-12 h-12 object-contain mx-auto mb-3" />
          <h1 className="heavy-heading text-2xl font-extrabold text-ink">Admin Panel</h1>
          <p className="text-xs text-muted-g mt-1">Manage your website photos</p>
        </div>
        <div>
          <label htmlFor="admin-email" className="label-caps text-muted-g block mb-2">Email</label>
          <input
            id="admin-email"
            type="email"
            required
            data-testid="admin-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 bg-bg border border-line-2 rounded-lg text-sm focus:outline-none focus:border-forest"
            placeholder="admin email"
          />
        </div>
        <div>
          <label htmlFor="admin-password" className="label-caps text-muted-g block mb-2">Password</label>
          <input
            id="admin-password"
            type="password"
            required
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 bg-bg border border-line-2 rounded-lg text-sm focus:outline-none focus:border-forest"
            placeholder="password"
          />
        </div>
        <button type="submit" disabled={busy} data-testid="admin-login-button" className="btn-primary w-full disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />}
          {busy ? "Signing in..." : "Sign In"}
        </button>
        <a href="/" data-testid="admin-back-home" className="block text-center text-xs text-muted-g hover:text-ink">
          ← Back to website
        </a>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [images, setImages] = useState({ hero: "", gallery: [] });
  const [heroInput, setHeroInput] = useState("");
  const [galleryUrl, setGalleryUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const heroFileRef = useRef(null);
  const galleryFileRef = useRef(null);

  const load = async () => {
    const res = await axios.get(`${API}/site-images`);
    setImages(res.data);
    setHeroInput(res.data.hero);
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load current images"));
  }, []);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await axios.post(`${API}/admin/upload`, fd, { headers: authHeaders() });
    return res.data.url;
  };

  const saveHero = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/site-images`, { hero: heroInput }, { headers: authHeaders() });
      setImages(res.data);
      toast.success("Hero photo updated — live on the website now");
    } catch {
      toast.error("Could not save hero photo");
    } finally {
      setSaving(false);
    }
  };

  const saveGallery = async (gallery) => {
    try {
      const res = await axios.put(`${API}/admin/site-images`, { gallery }, { headers: authHeaders() });
      setImages(res.data);
    } catch {
      toast.error("Could not update gallery");
    }
  };

  const addGalleryUrl = async () => {
    if (!galleryUrl.trim()) return;
    await saveGallery([...images.gallery, galleryUrl.trim()]);
    setGalleryUrl("");
    toast.success("Photo added to gallery");
  };

  const addGalleryFile = async (file) => {
    try {
      const url = await upload(file);
      await saveGallery([...images.gallery, url]);
      toast.success("Photo uploaded to gallery");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const heroFile = async (file) => {
    try {
      const url = await upload(file);
      setHeroInput(url);
      const res = await axios.put(`${API}/admin/site-images`, { hero: url }, { headers: authHeaders() });
      setImages(res.data);
      toast.success("Hero photo uploaded — live on the website now");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-bg">
      <header className="bg-white border-b border-line">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={STUDIO.logo} alt="Room 9" className="w-9 h-9 object-contain" />
            <div>
              <p className="font-extrabold text-ink leading-none">Room 9 Admin</p>
              <p className="text-[11px] text-muted-g">Website photo manager</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site" className="btn-ghost !px-4 !py-2">View site</a>
            <button onClick={onLogout} data-testid="admin-logout-button" className="btn-primary !px-4 !py-2">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10 space-y-10">
        {/* Hero image */}
        <section className="bg-white border border-line-2 rounded-xl p-6 sm:p-8">
          <h2 className="heavy-heading text-xl font-extrabold text-ink mb-1">Hero Photo</h2>
          <p className="text-xs text-muted-g mb-6">The large photo shown at the top of your homepage.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
            <div className="rounded-lg overflow-hidden border border-line aspect-[4/3] bg-bg">
              {images.hero && (
                <img src={imgSrc(images.hero)} alt="Current hero" data-testid="admin-hero-preview" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="space-y-4">
              <input
                data-testid="admin-hero-url-input"
                value={heroInput}
                onChange={(e) => setHeroInput(e.target.value)}
                placeholder="Paste image URL…"
                className="w-full px-4 py-3 bg-bg border border-line-2 rounded-lg text-sm focus:outline-none focus:border-forest"
              />
              <div className="flex gap-3">
                <button onClick={saveHero} disabled={saving} data-testid="admin-hero-save-button" className="btn-primary !px-5 !py-2.5">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />} Save URL
                </button>
                <button onClick={() => heroFileRef.current?.click()} data-testid="admin-hero-upload-button" className="btn-ghost !px-5 !py-2.5">
                  <Upload size={13} /> Upload photo
                </button>
                <input
                  ref={heroFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="admin-hero-file-input"
                  onChange={(e) => e.target.files?.[0] && heroFile(e.target.files[0])}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="bg-white border border-line-2 rounded-xl p-6 sm:p-8">
          <h2 className="heavy-heading text-xl font-extrabold text-ink mb-1">Studio Gallery</h2>
          <p className="text-xs text-muted-g mb-6">
            Photos of your actual space shown in the Gallery section. Add or remove anytime — changes go live instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <input
              data-testid="admin-gallery-url-input"
              value={galleryUrl}
              onChange={(e) => setGalleryUrl(e.target.value)}
              placeholder="Paste image URL…"
              className="flex-1 px-4 py-3 bg-bg border border-line-2 rounded-lg text-sm focus:outline-none focus:border-forest"
            />
            <button onClick={addGalleryUrl} data-testid="admin-gallery-add-button" className="btn-primary !px-5 !py-2.5">
              <Plus size={13} /> Add URL
            </button>
            <button onClick={() => galleryFileRef.current?.click()} data-testid="admin-gallery-upload-button" className="btn-ghost !px-5 !py-2.5">
              <Upload size={13} /> Upload photo
            </button>
            <input
              ref={galleryFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="admin-gallery-file-input"
              onChange={(e) => e.target.files?.[0] && addGalleryFile(e.target.files[0])}
            />
          </div>
          {images.gallery.length === 0 ? (
            <p data-testid="admin-gallery-empty" className="text-sm text-muted-g border border-dashed border-line-2 rounded-lg p-8 text-center">
              No gallery photos yet — add your first studio photo above.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {images.gallery.map((url, i) => (
                <div key={url + i} data-testid={`admin-gallery-item-${i}`} className="relative group rounded-lg overflow-hidden border border-line aspect-[4/3] bg-bg">
                  <img src={imgSrc(url)} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => saveGallery(images.gallery.filter((_, j) => j !== i))}
                    data-testid={`admin-gallery-delete-${i}`}
                    aria-label={`Remove photo ${i + 1}`}
                    className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Props & backdrop category photos (max 4 each) */}
        <CategoryPhotos images={images} onSaved={setImages} upload={upload} />

        {/* Customer reviews */}
        <ReviewsManager />
      </main>
    </div>
  );
}

function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const [name, setName] = useState("");
  const [rating, setRating] = useState("5");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const res = await axios.get(`${API}/reviews`);
    setReviews(res.data.reviews);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/reviews`,
        { name, rating: parseInt(rating, 10), text },
        { headers: authHeaders() }
      );
      setName("");
      setText("");
      setRating("5");
      await load();
      toast.success("Review published — live on the website now");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add review");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: authHeaders() });
      await load();
      toast.success("Review removed");
    } catch {
      toast.error("Could not remove review");
    }
  };

  return (
    <section className="bg-white border border-line-2 rounded-xl p-6 sm:p-8">
      <h2 className="heavy-heading text-xl font-extrabold text-ink mb-1">Customer Reviews</h2>
      <p className="text-xs text-muted-g mb-6">
        Add genuine customer reviews with name and star rating — they appear in the "Why Creators
        Pick Room 9" section. Only add reviews real customers have given you.
      </p>
      <form data-testid="admin-review-form" onSubmit={add} className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-4 mb-4">
        <input
          data-testid="admin-review-name-input"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Customer name, e.g. Priya S."
          className="px-4 py-3 bg-bg border border-line-2 rounded-lg text-sm focus:outline-none focus:border-forest"
        />
        <select
          data-testid="admin-review-rating-select"
          value={rating}
          onChange={(e) => setRating(e.target.value)}
          className="px-4 py-3 bg-bg border border-line-2 rounded-lg text-sm focus:outline-none focus:border-forest"
        >
          {[5, 4, 3, 2, 1].map((r) => (
            <option key={r} value={r}>{r} ★</option>
          ))}
        </select>
        <textarea
          data-testid="admin-review-text-input"
          required
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What the customer said…"
          className="sm:col-span-2 px-4 py-3 bg-bg border border-line-2 rounded-lg text-sm focus:outline-none focus:border-forest resize-none"
        />
        <button type="submit" disabled={busy} data-testid="admin-review-add-button" className="btn-primary !px-5 !py-2.5 sm:col-span-2 sm:w-fit disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Publish review
        </button>
      </form>
      {reviews.length === 0 ? (
        <p data-testid="admin-reviews-empty" className="text-sm text-muted-g border border-dashed border-line-2 rounded-lg p-6 text-center">
          No reviews yet — add your first genuine customer review above.
        </p>
      ) : (
        <ul className="space-y-3">
          {reviews.map((r, i) => (
            <li
              key={r.id}
              data-testid={`admin-review-item-${i}`}
              className="flex items-start justify-between gap-4 bg-bg border border-line rounded-lg p-4"
            >
              <div>
                <p className="text-sm font-bold text-ink">
                  {r.name} <span className="text-amber-500 font-mono text-xs ml-2">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                </p>
                <p className="text-sm text-muted-gd mt-1">"{r.text}"</p>
              </div>
              <button
                onClick={() => remove(r.id)}
                data-testid={`admin-review-delete-${i}`}
                aria-label={`Delete review by ${r.name}`}
                className="text-red-600 hover:bg-red-50 p-2 rounded-lg shrink-0"
              >
                <Trash2 size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CategoryPhotos({ images, onSaved, upload }) {
  const [urlInputs, setUrlInputs] = useState({});
  const fileRefs = useRef({});
  const cats = images.categories || {};

  const save = async (categories) => {
    try {
      const res = await axios.put(
        `${API}/admin/site-images`,
        { categories },
        { headers: authHeaders() }
      );
      onSaved(res.data);
      return true;
    } catch {
      toast.error("Could not save photos");
      return false;
    }
  };

  const addUrl = async (id) => {
    const url = (urlInputs[id] || "").trim();
    if (!url) return;
    if (await save({ ...cats, [id]: [...(cats[id] || []), url].slice(0, 4) })) {
      setUrlInputs((s) => ({ ...s, [id]: "" }));
      toast.success("Photo added — live on the website now");
    }
  };

  const addFile = async (id, file) => {
    try {
      const url = await upload(file);
      if (await save({ ...cats, [id]: [...(cats[id] || []), url].slice(0, 4) })) {
        toast.success("Photo uploaded — live on the website now");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const remove = async (id, i) => {
    if (await save({ ...cats, [id]: (cats[id] || []).filter((_, j) => j !== i) })) {
      toast.success("Photo removed");
    }
  };

  return (
    <section className="bg-white border border-line-2 rounded-xl p-6 sm:p-8">
      <h2 className="heavy-heading text-xl font-extrabold text-ink mb-1">Props & Backdrop Photos</h2>
      <p className="text-xs text-muted-g mb-6">
        Add up to 4 photos for each item — they appear on the Props and Backdrops cards on the
        website. Visitors can click them to zoom.
      </p>
      <div className="space-y-6">
        {PHOTO_CATEGORIES.map((c) => {
          const photos = cats[c.id] || [];
          const full = photos.length >= 4;
          return (
            <div key={c.id} data-testid={`category-panel-${c.id}`} className="border border-line rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-bold text-sm text-ink">{c.label}</p>
                <span className="font-mono text-[11px] text-muted-g">{photos.length}/4 photos</span>
              </div>
              {photos.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {photos.map((url, i) => (
                    <div key={url + i} className="relative group aspect-square rounded-lg overflow-hidden border border-line bg-bg">
                      <img src={imgSrc(url)} alt={`${c.label} ${i + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => remove(c.id, i)}
                        data-testid={`category-${c.id}-delete-${i}`}
                        aria-label={`Remove ${c.label} photo ${i + 1}`}
                        className="absolute top-1 right-1 bg-red-600 text-white p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {!full && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    data-testid={`category-${c.id}-url-input`}
                    value={urlInputs[c.id] || ""}
                    onChange={(e) => setUrlInputs((s) => ({ ...s, [c.id]: e.target.value }))}
                    placeholder="Paste image URL…"
                    className="flex-1 px-3 py-2.5 bg-bg border border-line-2 rounded-lg text-sm focus:outline-none focus:border-forest"
                  />
                  <div className="flex gap-2">
                    <button onClick={() => addUrl(c.id)} data-testid={`category-${c.id}-add-button`} className="btn-primary !px-4 !py-2">
                      <Plus size={13} /> Add
                    </button>
                    <button onClick={() => fileRefs.current[c.id]?.click()} data-testid={`category-${c.id}-upload-button`} className="btn-ghost !px-4 !py-2">
                      <Upload size={13} /> Upload
                    </button>
                    <input
                      ref={(el) => (fileRefs.current[c.id] = el)}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid={`category-${c.id}-file-input`}
                      onChange={(e) => e.target.files?.[0] && addFile(c.id, e.target.files[0])}
                    />
                  </div>
                </div>
              )}
              {full && <p className="text-[11px] text-muted-g">Limit reached — delete a photo to add a new one.</p>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
